import { type ColumnDef, type ColumnVisibilityState, createColumnHelper } from "@tanstack/react-table";
import { CircleSlash, Columns3, Copy, ExternalLink, MoreHorizontal, RotateCw, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { DataTable, type DataTableFeatures } from "~/components/data-table";
import { EmptyState } from "~/components/empty-state";
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
} from "~/components/ui";
import { useReferenceList } from "~/features/references/hooks";
import type { ReferenceListFilters, ReferenceRow } from "~/features/references/types";
import { toReferenceRowList } from "~/features/references/utils/transformers";

const DEFAULT_PAGE_SIZE = 25;

const REFERENCE_COLUMNS = [
  { id: "seqId", labelKey: "model.reference.seqID" },
  { id: "title", labelKey: "model.reference.title" },
  { id: "shortCode", labelKey: "model.reference.shortCode" },
  { id: "isbn", labelKey: "model.reference.isbn" },
] as const;

interface ReferencesTableProps {
  filters: ReferenceListFilters;
  onSelect: (id: string) => void;
}

const helper = createColumnHelper<DataTableFeatures, ReferenceRow>();

export function ReferencesTable({ filters, onSelect }: ReferencesTableProps) {
  const { t } = useTranslation();
  const { data, refetch } = useReferenceList();
  const [search, setSearch] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>({});
  const rows = useMemo(() => toReferenceRowList(data.references, filters), [data.references, filters]);

  const isVisible = (id: string) => columnVisibility[id] !== false;
  const setVisibility = (id: string, next: boolean) => setColumnVisibility((prev) => ({ ...prev, [id]: next }));
  const toggleAll = (next: boolean) =>
    setColumnVisibility(Object.fromEntries(REFERENCE_COLUMNS.map((c) => [c.id, next])));

  const onSearchChange = (next: string) => {
    setSearch(next);
    setPageIndex(0);
  };

  const handleCopyId = async (id: string) => {
    try {
      await copyToClipboard(id);
      toast.success(t("feature.common.idCopied"));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };

  const columns: ColumnDef<DataTableFeatures, ReferenceRow, any>[] = [
    helper.display({
      id: "deactivated",
      header: () => "",
      cell: (info) =>
        info.row.original.isDeactivated ? (
          <CircleSlash className="h-4 w-4 text-warning" aria-label={t("ui.deactivated")} />
        ) : null,
    }),
    helper.accessor("seqId", {
      id: "seqId",
      header: () => t("model.reference.seqID"),
      cell: (info) => <span className="tabular-nums text-fg-2">{info.getValue()}</span>,
    }),
    helper.accessor("title", {
      id: "title",
      header: () => t("model.reference.title"),
      cell: (info) => (
        <span className="block w-[28rem] whitespace-normal break-words font-medium text-fg-1">{info.getValue()}</span>
      ),
    }),
    helper.accessor("shortCode", {
      id: "shortCode",
      header: () => t("model.reference.shortCode"),
      cell: (info) => <span className="text-fg-2">{info.getValue()}</span>,
    }),
    helper.accessor("isbn", {
      id: "isbn",
      header: () => t("model.reference.isbn"),
      cell: (info) => <span className="tabular-nums text-fg-2">{info.getValue() || "—"}</span>,
    }),
    helper.display({
      id: "actions",
      header: () => "",
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center justify-end gap-1.5">
            <Button type="button" variant="outline" size="sm" onClick={() => onSelect(row.id)}>
              {t("ui.view")}
            </Button>
            {row.documentUrl && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                asChild
                aria-label={t("model.dataImportProject.document")}
              >
                <a href={row.documentUrl} target="_blank" rel="noreferrer">
                  <ExternalLink />
                </a>
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="icon" aria-label={t("ui.operations")}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => handleCopyId(row.id)}>
                  <Copy />
                  <span>{t("ui.copyId")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    }),
  ];

  if (rows.length === 0) {
    return (
      <EmptyState title={t("feature.references.empty.title")} description={t("feature.references.empty.description")} />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-3" />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("ui.search")}
            className="pl-8"
          />
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" size="icon" aria-label={t("ui.columns")}>
                <Columns3 />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              <DropdownMenuItem onSelect={() => toggleAll(false)}>{t("ui.hideAll")}</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => toggleAll(true)}>{t("ui.showAll")}</DropdownMenuItem>
              <DropdownMenuSeparator />
              {REFERENCE_COLUMNS.map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.id}
                  checked={isVisible(col.id)}
                  onCheckedChange={(checked) => setVisibility(col.id, checked === true)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {t(col.labelKey)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => void refetch()}
            aria-label={t("ui.reload")}
          >
            <RotateCw />
          </Button>
        </div>
      </div>
      <DataTable<ReferenceRow>
        columns={columns}
        data={rows}
        globalFilter={search}
        onGlobalFilterChange={onSearchChange}
        pageIndex={pageIndex}
        onPageIndexChange={setPageIndex}
        pageSize={pageSize}
        onPageSizeChange={(next) => {
          setPageSize(next);
          setPageIndex(0);
        }}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        emptyMessage={t("feature.common.noData")}
      />
    </div>
  );
}
